import type { LocationOption } from './api';

/**
 * Folds a place name or a typed query down to what matching should ignore.
 *
 * Field names are not typed the way they are stored. The seed has `Room101`, a sticker
 * reads `room-302`, and somebody hunting for either types "room 101" or "room302" with a
 * thumb. Case, spacing, punctuation and accents are all noise here, so all four are
 * removed from both sides before comparing — which is what makes "room 101" find
 * `Room101` and "302" find both `Room 302` and the token `room-302`.
 *
 * Accents matter because the second locale is Spanish: `Planta Ático` has to be findable
 * by someone typing "atico" on a keyboard they are not fighting.
 */
export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^\p{Letter}\p{Number}]/gu, '');
}

export interface PlaceMatch {
  place: LocationOption;
  /** Ancestor names, outermost first. Empty for a top-level place. */
  path: string[];
}

/**
 * The places whose name or QR token contains the query.
 *
 * Matches the place itself and not its ancestors, so every row shown contains what was
 * typed — searching "main" does not silently return the entire building. The tree is
 * still there for browsing; this is for when you already know the answer.
 *
 * Results keep the order the API sent (name-ascending) rather than being ranked. A
 * stable, explicable order beats a cleverer one nobody can predict while wearing gloves.
 */
export function searchPlaces(places: LocationOption[], query: string): PlaceMatch[] {
  const needle = fold(query);
  if (!needle) return [];

  const byId = new Map(places.map((place) => [place.id, place]));

  return places
    .filter(
      (place) =>
        fold(place.name).includes(needle) ||
        (place.qrCode !== null && fold(place.qrCode).includes(needle)),
    )
    .map((place) => ({ place, path: ancestorNames(place, byId) }));
}

/**
 * Walks up the tree to name where a place is.
 *
 * A flat result list has to say which floor `Room 101` is on, or it can send someone to
 * the wrong end of the property with no way to tell from the screen.
 *
 * The `seen` set is not decoration: a parentId cycle would spin here forever and hang the
 * one screen that must never fail. Nothing should create one — the edit route refuses to
 * re-parent a place under its own descendant — but this render is not the place to find
 * out that something did.
 */
export function ancestorNames(
  place: LocationOption,
  byId: ReadonlyMap<string, LocationOption>,
): string[] {
  const names: string[] = [];
  const seen = new Set<string>([place.id]);

  let current = place.parentId ? byId.get(place.parentId) : undefined;
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    names.unshift(current.name);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }

  return names;
}
